import React, { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { SavedPathway } from '../../../types/pathway';
import { SelectDropdown } from '../../SelectDropdown';
import { ResetFiltersButton } from '../../ResetFiltersButton';
import { PathwayButton } from '../PathwayButton';
import { SavedPathwayRow } from './SavedPathwayRow';
interface SavedPathwaysViewProps {
  pathways: SavedPathway[];
  onOpen: (pathway: SavedPathway) => void;
  onDuplicate: (pathway: SavedPathway) => void;
  onBuildNew: () => void;
}
export function SavedPathwaysView({
  pathways,
  onOpen,
  onDuplicate,
  onBuildNew
}: SavedPathwaysViewProps) {
  const [orgFilter, setOrgFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const organizations = useMemo(() => Array.from(new Set(pathways.map(p => p.organization))).sort(), [pathways]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = pathways.filter(pathway => {
      if (orgFilter !== 'all' && pathway.organization !== orgFilter) return false;
      if (needle && !`${pathway.name} ${pathway.organization}`.toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
    return filtered.slice().sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'company') return a.organization.localeCompare(b.organization) || a.name.localeCompare(b.name);
      if (sort === 'size') return b.moduleIds.length - a.moduleIds.length;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [pathways, orgFilter, query, sort]);
  const isFiltered = orgFilter !== 'all' || query.trim().length > 0 || sort !== 'recent';
  const resetFilters = () => {
    setOrgFilter('all');
    setQuery('');
    setSort('recent');
  };
  return <section aria-labelledby="saved-pathways-heading" data-id="element-1814" data-mp="2dqib709hq0t1">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4" data-id="element-1815" data-mp="2dnvx0hs3gn1u">
        <div data-id="element-1816" data-mp="28pudxwuo4nqd">
          <h2 id="saved-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-1817" data-mp="28ti7gw045a53">
            Your pathways{pathways.length > 0 ? ` (${pathways.length})` : ''}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-1818" data-mp="2wgqa1665q53f">
            Pick up something you started, or duplicate a pathway that worked
            for a new prospect.
          </p>
        </div>
        <PathwayButton onClick={onBuildNew} className="whitespace-nowrap px-8" data-id="element-1819" data-mp="2nog07pz6c1mp">
          <Plus size={18} data-id="element-1820" data-mp="2nmbq2qaom6dz" />
          Build a New Pathway
        </PathwayButton>
      </div>

      {pathways.length > 0 && <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1821" data-mp="3g4h0i429r4nm">
          <label className="relative" data-id="element-1822" data-mp="3g6tooyymeb94">
            <span className="sr-only" data-id="element-1823" data-mp="3bdh0gpxus6gw">Search pathways</span>
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1824" data-mp="3bbxmx73l09it" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name or company" className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1825" data-mp="3npb0xcxvs45s" />
          </label>
          <SelectDropdown ariaLabel="Filter pathways by company" scrollMenu value={orgFilter} onChange={setOrgFilter} options={[{
        value: 'all',
        label: 'All companies'
      }, ...organizations.map(org => ({
        value: org,
        label: org
      }))]} data-id="element-1826" data-mp="agogudjthqe8" />
          <SelectDropdown ariaLabel="Sort pathways" value={sort} onChange={setSort} options={[{
        value: 'recent',
        label: 'Recently updated'
      }, {
        value: 'name',
        label: 'Pathway name'
      }, {
        value: 'company',
        label: 'Company'
      }, {
        value: 'size',
        label: 'Most modules'
      }]} data-id="element-1827" data-mp="o71sapvx3n8b" />
          <ResetFiltersButton isActive={isFiltered} onReset={resetFilters} data-id="element-1828" data-mp="pdenhh9iuvf1" />
        </div>}

      {pathways.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1829" data-mp="3srh981x7dt4j">
          <h3 className="text-base font-bold text-gray-900" data-id="element-1830" data-mp="3oqxbaw4t2lhc">
            No pathways yet
          </h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-600" data-id="element-1831" data-mp="6l2kqteyhs4d">
            Build one from a conversation you just had, or start from a proven
            pathway below.
          </p>
        </div> : visible.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1832" data-mp="1hy4ognvyin74">
          <p className="text-sm text-gray-600" data-id="element-1833" data-mp="1d0mqttne3uef">
            No pathways match those filters.
          </p>
          <button type="button" onClick={resetFilters} className="mt-2 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1834" data-mp="2v0m0a740wko8">
            Clear filters
          </button>
        </div> : <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1835" data-mp="2qxxphtvr13om">
          <div className="overflow-x-auto" data-id="element-1836" data-mp="3enjq8lrrrqyq">
            <table className="w-full table-fixed sm:min-w-[760px]" data-id="element-1837" data-mp="3eous2g1nmon7">
              <caption className="sr-only" data-id="element-1838" data-mp="3inkeey4o7giz">
                Saved pathways — expand a row to see its modules

              </caption>
              <colgroup data-id="element-1839" data-mp="34uupolxrbxi6">
                <col data-id="element-1840" data-mp="39v8odddgxv3j" />
                <col className="w-[200px]" data-id="element-1841" data-mp="39uqma044kmnp" />
                <col className="w-[110px]" data-id="element-1842" data-mp="39tffrsjap0sw" />
                <col className="w-[190px]" data-id="element-1843" data-mp="1fp2x0j1hgz1" />
              </colgroup>
              <thead data-id="element-1844" data-mp="66oz23kc3v0h">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1845" data-mp="66x8tml5uk7g">
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1846" data-mp="g2nnqcne5d4s">
                    Pathway
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1847" data-mp="b4cea310d4n3">
                    Audience
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1848" data-mp="14d6i8cexiba">
                    Modules
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1849" data-mp="3oiqxqp1z7dwa">
                    <span className="sr-only" data-id="element-1850" data-mp="3jil26xsl578x">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1851" data-mp="3elm5q8lg8o7k">
                {visible.map(pathway => <SavedPathwayRow key={pathway.id} pathway={pathway} expanded={expandedId === pathway.id} onToggleExpand={() => setExpandedId(expandedId === pathway.id ? null : pathway.id)} onOpen={onOpen} onDuplicate={onDuplicate} data-id="element-1852" data-mp="39lq2ibkkt0t2" />)}
              </tbody>
            </table>
          </div>
        </div>}
    </section>;
}